import { useAlert } from '~/components/notifications/useAlert';
import { AlertLevel } from '~/enums/Alert';

enum NotificationType {
  FORM = 'FORM',
  ALERT = 'ALERT',
}

export type ServerAlertError = 'success' | 'warning' | 'info' | 'error';

export interface NotificationDetails {
  level?: AlertLevel;
  type?: NotificationType;
  title?: string;
}

export interface FluzError {
  errorRecord: {
    code: string;
    name: string;
    message: string;
    status_code: number;
    user_friendly: boolean;
    notification_details: NotificationDetails;
  };
}

export interface ResponseError {
  success: boolean;
  statusCode: number;
  msg: string;
  error: FluzError | Record<string, unknown>;
}

const fallbackTitle = 'OOPS SOMETHING WENT WRONG';
const fallbackMsg =
  'Sorry, we ran into an issue while processing your request, please refresh the page and try again or contact customer support';

export function useErrorHandler() {
  const { addAlert } = useAlert();

  function showErrorAlert(title: string, bodyText: string, state: AlertLevel = 'error') {
    addAlert({
      title,
      bodyText,
      state,
      customContainerClass: 'server-error-alert',
    });
  }

  function parseResponseError(responseError: ResponseError, canHandleFormError = false) {
    const statusCode = responseError && responseError.statusCode ? responseError.statusCode : null;
    const isFluzError = !!responseError.error?.errorRecord;
    let displayTitle = fallbackTitle;
    let displayMessage = responseError.msg ?? fallbackMsg;
    if (isFluzError) {
      const { errorRecord } = responseError.error as FluzError;
      const { message, notification_details } = errorRecord;
      const { level, type, title } = notification_details || {};
      displayTitle = title ?? fallbackTitle;
      displayMessage = message;
      if (type === 'FORM' && canHandleFormError) {
        return {
          type,
          message,
          level,
        };
      } else {
        showErrorAlert(displayTitle, message, level);
      }
    } else if (statusCode === 498) {
      return;
    } else {
      showErrorAlert(displayTitle, displayMessage);
    }
  }
  return { parseResponseError, showErrorAlert };
}

export function parseServerResponseError(responseError: ResponseError) {
  const statusCode = responseError && responseError.statusCode ? responseError.statusCode : null;
  const isFluzError = !!responseError?.error?.errorRecord;
  let displayTitle = fallbackTitle;
  let displayMessage = responseError?.msg ?? fallbackMsg;
  let displayLevel: ServerAlertError = 'error' as unknown as ServerAlertError;

  if (isFluzError) {
    const { errorRecord } = responseError?.error as FluzError;
    const { message, notification_details } = errorRecord;
    const { level, title } = notification_details || {};
    displayTitle = title ?? fallbackTitle;
    displayMessage = message;

    if (level !== 'danger') {
      displayLevel = level as unknown as ServerAlertError;
    }
  }

  return {
    displayTitle,
    displayMessage,
    displayLevel,
    statusCode,
  };
}
